import { CeresB2BDiscount } from '../../components/b2bDiscountMessages';

/**
 * Fetch product display data for a set of variant ids.
 *
 * A saved list only stores `{ id (variantId), quantity, selected }`. The detail
 * view needs the product display data (title, brand, image, sku, stock, price)
 * resolved from the variant ids at view time.
 *
 * Returns a map keyed by variant id:
 *   { [variantId]: { id, brand, title, sku, image, stock,
 *                    priceLabel, unitPrice, taxLabel, category, eta } }
 *
 * PRICING — IMPORTANT:
 * The prices shown in lists are ultimately COMPANY-SPECIFIC (B2B): each company
 * sees its own CTN price / per-unit price / volume pricing. Resolving that
 * requires company context (a backend endpoint taking { variant_ids, company_id },
 * or a Liquid section render) — a plain Storefront API call returns base prices
 * only, not company prices.
 *
 * TODO(b2b-pricing): wire company-specific pricing. Deferred for now.
 * TODO(data-source): until then, the real branch should fetch BASE product data
 * + BASE price for the given variant ids (e.g. Storefront API once a Ceres
 * token is configured). Base price is a placeholder until B2B pricing lands.
 */

// Demo dataset keyed by the real variant ids, so `?yl-demo=1` renders a
// realistic detail view before the real endpoint exists.
const DEMO_PRODUCT_DATA = {
  '42484596572254': {
    brand: 'Ceres Organics', category: 'Savoury Snacks', sku: '1234567',
    title: 'Organic Roasted Seaweed, Salt & Vinegar Nori Snack - 5g',
    stock: 'in', maxQty: 50, priceLabel: '$11.00 CTN6', unitPrice: '$1.83', taxLabel: 'GST FREE',
    url: '/products/organic-roasted-seaweed-salt-vinegar-nori-snack-5g',
    badge: 'Certified Organic', onSale: true,
  },
  '42716782231646': {
    brand: 'Ceres Organics', category: 'Savoury Snacks', sku: '1234567',
    title: 'Organic Roasted Seaweed, Salt & Vinegar Nori Snack - 5g',
    stock: 'low', maxQty: 5, priceLabel: '$11.00 CTN6', unitPrice: '$1.83', taxLabel: 'GST FREE',
    url: '/products/organic-roasted-seaweed-salt-vinegar-nori-snack-5g',
    badge: 'Certified Organic', tags: ['new'],
  },
  '42716782821470': {
    brand: 'Ceres Organics', category: 'Savoury Snacks', sku: '1234567',
    title: 'Organic Roasted Seaweed Multipack, Salt & Vinegar Snack, - 8 x 2g',
    stock: 'in', maxQty: 30, priceLabel: '$11.00 CTN6', unitPrice: '$1.83', taxLabel: 'GST FREE',
    url: '/products/organic-roasted-seaweed-multipack-salt-vinegar-snack-8x2g',
    badge: 'Certified Organic',
  },
  '42716901212254': {
    brand: 'Ceres Organics', category: 'Savoury Snacks', sku: '1234567',
    title: 'Organic Puffed Stars Sour Cream & Chives - 80g',
    stock: 'out', maxQty: 0, priceLabel: '$11.00 CTN6', unitPrice: '$1.83', taxLabel: 'GST FREE',
    url: '/products/organic-puffed-stars-sour-cream-chives-80g',
    badge: '', eta: 'Mid October',
  },
};

function isDemo() {
  return (
    typeof window !== 'undefined' &&
    window.location.search.includes('yl-demo')
  );
}

async function fetchDemoProducts(variantIds) {
  // Simulate network latency so loading states are exercised.
  await new Promise((resolve) => setTimeout(resolve, 400));
  return variantIds.reduce((acc, id) => {
    if (DEMO_PRODUCT_DATA[id]) acc[id] = { id, ...DEMO_PRODUCT_DATA[id] };
    return acc;
  }, {});
}

// Below this many available units a variant is shown as "Low Stock".
const LOW_STOCK_THRESHOLD = 10;

const VARIANTS_QUERY = `
  query getVariants($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on ProductVariant {
        id
        sku
        availableForSale
        quantityAvailable
        price { amount currencyCode }
        compareAtPrice { amount currencyCode }
        image { url altText }
        product {
          id
          title
          vendor
          handle
          productType
          tags
          onlineStoreUrl
          featuredImage { url altText }
          badgesMf: metafield(namespace: "custom", key: "badges_to_display_plp") { value }
          categoryMf: metafield(namespace: "custom", key: "category") { value }
          productNumberMf: metafield(namespace: "custom", key: "product_number") { value }
          etaMf: metafield(namespace: "custom", key: "inventory_arrival_message") { value }
        }
      }
    }
  }`;

/** Numeric variant id -> Storefront global id. */
const toGid = (id) => `gid://shopify/ProductVariant/${id}`;

/** Global id -> trailing numeric id (matches the saved list item id). */
const stripGid = (gid) => String(gid).split('/').pop();

/** Format a Money value as e.g. "$11.00". */
function formatMoney(money) {
  if (!money || money.amount == null) return '';
  return `$${Number(money.amount).toFixed(2)}`;
}

// First value of the product's `custom.badges_to_display_plp` list metafield
// (mirrors `... | first` in card-product.liquid). Empty string = no badge.
function firstBadge(product) {
  const raw = product.badgesMf?.value;
  if (!raw) return '';
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed[0] || '' : String(parsed);
  } catch {
    return raw;
  }
}

// Group heading from the product's `custom.category` list metafield.
// We use the SECOND level (index 1); if the list is shorter, fall back to the
// last available level. Empty string = no value from the metafield (caller
// falls back to `product.productType`).
function categoryGroup(product) {
  const raw = product.categoryMf?.value;
  if (!raw) return '';
  let list;
  try {
    list = JSON.parse(raw);
  } catch {
    return raw;
  }
  if (!Array.isArray(list) || list.length === 0) return '';
  return list[1] || list[list.length - 1] || '';
}

// A resolved product is out of stock when marked 'out' or capped at 0.
export const isOutOfStock = (product) =>
  product.stock === 'out' || product.maxQty === 0;

function mapStock(node) {
  if (!node.availableForSale) return 'out';
  const qty = node.quantityAvailable;
  if (typeof qty === 'number' && qty > 0 && qty <= LOW_STOCK_THRESHOLD) {
    return 'low';
  }
  return 'in';
}

/** Map a Storefront ProductVariant node to our render shape. */
function mapVariantNode(node) {
  const id = stripGid(node.id);
  const product = node.product || {};
  // Mirrors price.liquid: on sale when compare_at_price exists and differs.
  const compareAt = node.compareAtPrice?.amount;
  const onSale =
    compareAt != null && Number(compareAt) !== Number(node.price?.amount);
  return {
    id,
    // Numeric product id — used by the recommendations API for OOS items.
    productId: product.id ? stripGid(product.id) : '',
    brand: product.vendor || '',
    // Fall back to the product's type when the `custom.category` metafield
    // has no value, so rows still get a group heading instead of falling
    // into "Uncategorised".
    category: categoryGroup(product) || product.productType || '',
    sku: node.sku || '',
    // Fallback identifier shown when the variant has no SKU.
    productNumber: product.productNumberMf?.value || '',
    title: product.title || '',
    // When the arrival message is due, i.e. only shown on out-of-stock rows.
    // Same metafield card-product.liquid reads for its ETA line.
    eta: product.etaMf?.value || '',
    // Rendered at most 225px wide / 140px tall in the list row; request a
    // ~2x thumbnail from the CDN instead of the full-resolution original.
    image: sizedImage(node.image?.url || product.featuredImage?.url || '', 260),
    handle: product.handle || '',
    // "Volume Pricing" links to the product page.
    url: product.onlineStoreUrl
      || (product.handle ? `/products/${product.handle}` : ''),
    badge: firstBadge(product),
    onSale,
    tags: product.tags || [],
    stock: mapStock(node),
    // Upper bound for the quantity input. 0 when not for sale; null when the
    // available quantity is unknown (inventory not exposed to the token).
    maxQty: node.availableForSale
      ? (typeof node.quantityAvailable === 'number' ? node.quantityAvailable : null)
      : 0,
    // TODO(b2b-pricing): base price only — replace with company CTN / per-unit
    // / volume pricing once company pricing is wired (see header).
    priceLabel: formatMoney(node.price),
    unitPrice: '',
    taxLabel: '',
  };
}

export async function fetchListProducts(variantIds, accessToken) {
  const ids = (variantIds || []).filter(Boolean);
  if (ids.length === 0) return {};

  if (isDemo()) return fetchDemoProducts(ids);

  if (!accessToken) {
    throw new Error('fetchListProducts: missing Storefront access token');
  }

  const storeDomain = window.Shopify?.shop;

  const res = await fetch(`https://${storeDomain}/api/2024-10/graphql.json`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': accessToken,
    },
    body: JSON.stringify({
      query: VARIANTS_QUERY,
      variables: { ids: ids.map(toGid) },
    }),
  });

  if (!res.ok) {
    throw new Error(`Storefront API error: ${res.status}`);
  }

  const json = await res.json();
  if (json.errors?.length) {
    throw new Error(json.errors[0]?.message || 'Storefront API error');
  }

  // `nodes` returns null for ids that don't resolve (e.g. deleted variant).
  const nodes = json.data?.nodes || [];
  return nodes.reduce((acc, node) => {
    if (node?.id && node.product) {
      acc[stripGid(node.id)] = mapVariantNode(node);
    }
    return acc;
  }, {});
}

/**
 * Fetch company-aware price HTML per variant via the Section Rendering API.
 *
 * The `your-lists-price` section renders `price.liquid` in the logged-in
 * customer's context, so `variant.price` (and CTN / per-unit / GST) reflect the
 * company's catalog pricing — which a plain Storefront call can't provide.
 *
 * The section also carries the product's discount groups, used to decide
 * whether the row offers Volume Pricing (same rule as PLP cards), its Level 2
 * category — the row's group heading, matched the way the mini cart does it
 * ('' when the product sits under no Level 2), with its Level 1 parent and the
 * parent's position in the `order` menu (null when unknown), used to sort the
 * groups — and its DEAL / SUPERDEAL flag
 * ('' for none). The price HTML carries the company's promotion the way PLP
 * cards show it (COB2B-785).
 *
 * `products` — array of the mapped products ({ id, handle }).
 * Returns a map keyed by variant id:
 *   { [variantId]: { html, hasVolume, category, categoryParent, categoryParentIndex, flag } }.
 */
export async function fetchListPrices(products) {
  // Demo mode has no real handles/section; the caller falls back to priceLabel.
  if (isDemo()) return {};

  const entries = await Promise.all(
    (products || [])
      .filter((p) => p && p.handle && p.id)
      .map(async (p) => {
        try {
          const res = await fetch(
            `/products/${p.handle}?section_id=your-lists-price&variant=${p.id}`,
          );
          return [p.id, res.ok ? parsePriceSection(await res.text(), p.id) : null];
        } catch {
          return [p.id, null];
        }
      }),
  );

  return Object.fromEntries(entries);
}

/** Split a your-lists-price render into its price HTML and Volume Pricing flag. */
function parsePriceSection(text, variantId) {
  const doc = new DOMParser().parseFromString(text, 'text/html');
  const groups = doc.querySelector('[data-yl-discount-groups]');
  let hasVolume = false;
  let category = '';
  let categoryParent = '';
  let categoryParentIndex = null;
  let flag = '';
  if (groups) {
    category = groups.dataset.category || '';
    categoryParent = groups.dataset.categoryParent || '';
    const index = parseInt(groups.dataset.categoryParentIndex, 10);
    categoryParentIndex = Number.isNaN(index) ? null : index;
    flag = groups.dataset.flag || '';
    const key = {
      productId: groups.dataset.productId,
      variantId,
      groupG5: groups.dataset.productGroupG5,
      groupG6: groups.dataset.productGroupG6,
    };
    hasVolume = CeresB2BDiscount.hasVolumePricing(key);
    groups.remove();
    // COB2B-785: show the company's current promotion on the price, as PLP
    // cards do — decorated here, before the HTML goes into the row.
    CeresB2BDiscount.decoratePriceBlock(doc.body, key);
  }
  return {
    html: doc.body.innerHTML,
    hasVolume,
    category,
    categoryParent,
    categoryParentIndex,
    flag,
  };
}

/**
 * Fetch one IN-STOCK recommended product for an out-of-stock item, via the
 * Shopify Product Recommendations API (public, no auth):
 *   /recommendations/products.json?product_id={id}&intent=related
 *
 * Returns { title, url, image, variantId } or null when there's no in-stock
 * recommendation.
 */
// Request a small render of a Shopify CDN image via the `width` param.
function sizedImage(url, width) {
  if (!url) return '';
  return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
}

export async function fetchRecommendation(productId) {
  if (!productId) return null;

  if (isDemo()) {
    return {
      title: 'Organic Coconut Sugar - 400 g',
      url: '/products/organic-coconut-sugar-400g',
      image: '',
      variantId: '42484665745502',
    };
  }

  try {
    const res = await fetch(
      `/recommendations/products.json?product_id=${productId}&intent=related&limit=10`,
    );
    if (!res.ok) return null;
    const { products } = await res.json();
    const rec = (products || []).find((p) => p.available);
    if (!rec) return null;
    return {
      title: rec.title,
      url: rec.url,
      image: sizedImage(rec.featured_image || rec.images?.[0] || '', 120),
      variantId: rec.variants?.[0]?.id, // for the add-to-cart button (later)
    };
  } catch {
    return null;
  }
}

export default fetchListProducts;
